import {
  Avatar,
  Box,
  Button,
  Chip,
  Modal,
  ModalClose,
  ModalDialog,
  Typography,
} from "@mui/joy";
import { lighten } from "@mui/material";
import axios from "axios";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Role, UserType } from "types";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate } from "react-router-dom";
import { useAuthorization } from "context/AuthorizationContext";

interface DetailsModalProps {
  open: boolean;
  userId: string;
  layout?: "center" | "fullscreen";
  onClose: () => void;
}

const UserDetailsModal: React.FC<DetailsModalProps> = ({
  open,
  userId,
  layout,
  onClose,
}) => {
  const [user, setUser] = useState<UserType>({
    id: "",
    firstName: "",
    lastName: "",
    email: "",
    roleId: "",
    isSystem: false,
    artists: [],
    lang: "",
    createdAt: "",
    updatedAt: null,
    deletedAt: null,
  });
  const [roles, setRoles] = useState<Role[]>([]);
  const [t] = useTranslation();
  const navigate = useNavigate();
  const { isAllowed } = useAuthorization();

  const fetchUser = useCallback(async () => {
    try {
      const { data } = await axios.get<{ data: UserType }>(
        `/api/users/${userId}`
      );
      setUser(data.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        console.log(error.response?.data);
      } else {
        console.log(error);
      }
    }
  }, [userId, navigate]);

  const fetchRoles = useCallback(async () => {
    try {
      const { data } = await axios.get<{ data: Role[] }>("/api/roles");
      setRoles(data.data);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        //Fore the user logout if the request is unauthorized
        if (error.response?.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        console.log(error.response?.data);
      } else {
        console.log(error);
      }
    }
  }, [navigate]);

  useEffect(() => {
    fetchRoles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchUser();
  }, [userId, fetchUser, fetchRoles]);

  return (
    <Modal open={open} onClose={onClose}>
      <ModalDialog
        sx={{
          p: 0,
          minWidth: layout && layout === "fullscreen" ? undefined : 400,
          bgcolor: (theme) => lighten(theme.palette.primary[200], 0.9),
        }}
        layout={layout ? layout : "center"}
        size="lg"
        aria-labelledby="user-detail-modal-title"
        aria-describedby="user-detail-modal-description"
      >
        <Box
          display="flex"
          flexDirection="column"
          width={"100%"}
          height={"100%"}
        >
          <ModalClose color="neutral" variant="plain" />
          {/* Modal body */}
          <Box flex={"1 1"} width={"100%"} height={"100%"}>
            {/* User main info container */}
            <Box
              display="flex"
              flexDirection="row"
              justifyContent={"flex-start"}
              alignItems={"center"}
              gap={2}
              p={2}
              height={100}
            >
              <Avatar
                color="primary"
                size="lg"
                sx={{ width: "5rem", height: "5rem" }}
              />

              <Box display="flex" flexDirection="column">
                <Typography level="h6">
                  {user.firstName} {user.lastName}
                </Typography>
                <Typography level="body2">{user.email}</Typography>
              </Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              flex={"1 1"}
              height={"calc(100% - 100px)"}
              rowGap={1}
              bgcolor={(theme) => theme.palette.background.body}
              p={2}
            >
              <Box
                display="flex"
                flexDirection="row"
                columnGap={2}
                justifyContent={"space-between"}
                width={"50%"}
              >
                {/* Role */}
                <Box display="flex" flexDirection="column">
                  <Typography level="body2" fontWeight={600}>
                    {t("Role")}
                  </Typography>
                  <Typography level="body1">
                    {roles.find((r) => r.id === user.roleId)?.name}
                  </Typography>
                </Box>
                {/* Lang */}
                <Box display="flex" flexDirection="column">
                  <Typography level="body2" fontWeight={600}>
                    {t("Language")}
                  </Typography>
                  <Typography level="body1">{user.lang}</Typography>
                </Box>
              </Box>
              {/* Artists */}
              <Box display="flex" flexDirection="column">
                <Typography level="body2" fontWeight={600}>
                  {t("Artists")}
                </Typography>
                <Box
                  display="flex"
                  flexDirection="row"
                  columnGap={1}
                  flexWrap={"wrap"}
                >
                  {user.artists ? (
                    user.artists.map((a) => (
                      <Chip key={a.id} variant="soft" color="neutral" size="sm">
                        {a.name}
                      </Chip>
                    ))
                  ) : (
                    <Typography level="body4">
                      {t("No artists assigned to this user")}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          {/* Modal footer */}
          <Box
            display="flex"
            flexDirection="row"
            bgcolor={(theme) => theme.palette.background.body}
            borderRadius={
              layout && layout === "fullscreen" ? 0 : "0px 0px 10px 10px"
            }
            p={2}
            justifyContent="flex-end"
          >
            {isAllowed("users.write") ? (
              <Button
                variant="plain"
                color="neutral"
                startDecorator={<EditIcon />}
                size="sm"
                onClick={() => {
                  navigate(userId);
                }}
              >
                {t("Edit")}
              </Button>
            ) : null}
          </Box>
        </Box>
      </ModalDialog>
    </Modal>
  );
};

export default UserDetailsModal;
